import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { FormValidator } from "../form.validator";
import * as strings from "../stringFiles/eventCategory.json";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { EventCategoryService } from "./event-category.service";
import { TranslateService } from "@ngx-translate/core";

declare var customToastr: any;

@Component({
    selector: "app-event-category",
    templateUrl: "./event-category.component.html",
    styleUrls: [
        "./event-category.component.css",
        "./../../assets/css/theme-default/bootstrap.css",
        "./../../assets/css/theme-default/materialadmin.css",
        "./../../assets/css/theme-default/font-awesome.min.css",
        "./../../assets/css/theme-default/material-design-iconic-font.min.css",
        "./../../assets/css/theme-default/libs/DataTables/jquery.dataTables.css",
        "./../../assets/css/theme-default/libs/bootstrap-datepicker/datepicker3.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/dataTables.colVis.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/buttons.dataTables.min.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/dataTables.tableTools.css",
        "./../../_crop/css/style.css",
        "./../../_crop/css/style-example.css",
    ],
    encapsulation: ViewEncapsulation.None,
})

export class EventCategoryComponent implements OnInit {
    isPageLoading = true;
    eventCategoryForm: FormGroup;
    strings = strings;
    isPopupVisible = false;
    isEditPopupVisible = false;
    showOverlay = false;
    isDeleteConfirmBox = false;
    createEventFormSubmit = false;
    eventCategoryData = [];
    colorList = [];
    conflictCategoryData = [{}];
    selectedColor: number | null = null;
    categoryId = null;
    language = "en";
    confirmDeletePopup: boolean = false;
    confirmDeletePopupForm: boolean = false;

    constructor(
        private router: Router,
        private fb: FormBuilder,
        private eventCategoryService: EventCategoryService,
        private translate: TranslateService
    ) {
        this.eventCategoryForm = this.fb.group({
            eventCategoryName: [
                { value: "", disabled: false },
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(200),
                    // this.alphaNumericWithSpacesValidator
                ],
            ],
            eventCategoryColor: [
                { value: "", disabled: false },
                [Validators.required],
            ],
        });
    }

    ngOnInit(): void {
        this.featureCheck();
        this.getEventCategoryColorList();
        this.getEventCategory();
    }

    // It checks this feature enable or not 
    featureCheck() {
        const extensionServices = localStorage.getItem('extensionServices');
        const data = JSON.parse(extensionServices);
        const featureEnabled = data.some(item => item.serviceName === 'ACADEMIC_CALENDAR')
        featureEnabled == true ?
            this.router.navigate(["/academic/events"]) : this.router.navigate(["***"])
    }

    // Check white spaces in the text field If the space is there in the starting and ending of the string it return error
    alphaNumericWithSpacesValidator(control: AbstractControl): { [key: string]: any } | null {
        const value = control.value;
        if (!value || !value.trim()) {
            return { invalidCharacter: true };
        }
        const regex = /^[a-zA-Z0-9\s]+$/;
        if (!regex.test(value)) {
            return { invalidCharacter: true };
        }
        return null;
    }

    // Get the event category list
    async getEventCategory() {
        try {
            this.isPageLoading = true;
            const response: any = await firstValueFrom(this.eventCategoryService.getEventCategory());
            if (response?.status) {
                this.eventCategoryData = response?.body?.categoryList;
            }
            this.isPageLoading = false;
        }
        catch (error) {
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const unableToFetchEventCategoryTranslation = await firstValueFrom(this.translate.get('unableToFetchEventCategory')); 
                customToastr(unableToFetchEventCategoryTranslation, "error");
            }
        }

    }

    // Set the color of the event category using colorId
    selectColor(pkColorId: number): void {
        if (this.selectedColor !== pkColorId) {
            this.selectedColor = pkColorId;
            this.eventCategoryForm.get('eventCategoryColor')?.setValue(pkColorId);
            this.eventCategoryForm.get('eventCategoryColor')?.markAsDirty();
            this.eventCategoryForm.markAsDirty();
        }
    }

    // Open create popup  
    openPopup() {
        this.isEditPopupVisible = false;
        this.showOverlay = true;
        setTimeout(() => {
            this.isPopupVisible = true;
        }, 300);
    }

    // Open edit popup
    editPopup(data: any) {
        this.categoryId = data.pkEventCategory;
        this.isEditPopupVisible = true;
        this.showOverlay = true;
        this.isPopupVisible = true;
        this.eventCategoryForm.patchValue({
            eventCategoryName: data.categoryName,
            eventCategoryColor: data.pkColorId,
        });
        this.selectedColor = data?.pkColorId;
        this.eventCategoryForm.markAsPristine();
    }

    // Open delete popup
    deletePopup(data: any) {
        this.categoryId = data.pkEventCategory;
        this.showOverlay = true;
        this.isDeleteConfirmBox = true;
    }

    // Function to close all the popups
    closePopup() {
        this.isEditPopupVisible = false;
        this.isDeleteConfirmBox = false;
        this.confirmDeletePopup = false;
        this.isPopupVisible = this.showOverlay = false;
        this.eventCategoryForm.reset({ eventCategoryName: "" });
        this.selectedColor = null;
        this.eventCategoryForm.get("eventCategoryColor")?.setValue(null);
    }

    // Disable the input form controls
    disableFormControls() {
        Object.keys(this.eventCategoryForm.controls).forEach((controlName) => {
            this.eventCategoryForm.get(controlName)?.disable();
        });
    }

    // Enable the input form controls
    enableFormControls() {
        Object.keys(this.eventCategoryForm.controls).forEach((controlName) => {
            this.eventCategoryForm.get(controlName)?.enable();
        });
    }

    // Function for submit the event category data on create and edit the event category 
    async onFormSubmit() {
        if (this.isEditPopupVisible) {
            this.editEventCategoryOnSubmit();
        }
        else {
            this.createEventCategoryOnSubmit();
        }
    }

    // Function for edit event category after clicking save button
    async editEventCategoryOnSubmit() {
        try {
            const eventCategoryName = this.eventCategoryForm
                .get("eventCategoryName")
                ?.value?.toString();
            const eventCategoryColor =
                this.eventCategoryForm.get("eventCategoryColor")?.value;
            if (eventCategoryName.trim() != '' && eventCategoryColor != '') {
                this.createEventFormSubmit = true;
                this.disableFormControls();
                const body = {
                    lang: this.language,
                    eventCategoryId: this.categoryId,
                    categoryName: eventCategoryName,
                    colorCode: eventCategoryColor,
                    academicId: this.eventCategoryService.academicId
                }
                const response: any = await firstValueFrom(
                    this.eventCategoryService.editEventCategory(body)
                );
                this.createEventFormSubmit = false;
                this.enableFormControls();
                if (response?.status) {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, "success");
                    }
                    else {
                        const editEventCategorySuccessMessageTranslation = await firstValueFrom(this.translate.get('editEventCategorySuccessMessage')); 
                        customToastr(editEventCategorySuccessMessageTranslation, "success");
                    }
                    this.closePopup();
                    this.getEventCategory();
                }
                else {
                    const CategoryFailedToCreateErrorTextTranslation = await firstValueFrom(this.translate.get('CategoryFailedToCreateErrorText'));  
                    customToastr(CategoryFailedToCreateErrorTextTranslation, "error");
                    this.getEventCategory();
                }
            }
        }
        catch (error) {
            this.createEventFormSubmit = false;
            this.enableFormControls();
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const CategoryFailedToEditErrorTextTranslation = await firstValueFrom(this.translate.get('CategoryFailedToEditErrorText')); 
                customToastr(CategoryFailedToEditErrorTextTranslation, "error");
            }
        }
    }

    // Function for create event category after clicking create button
    async createEventCategoryOnSubmit() {
        try {
            const eventCategoryName = this.eventCategoryForm
                .get("eventCategoryName")
                ?.value?.toString();
            const eventCategoryColor =
                this.eventCategoryForm.get("eventCategoryColor")?.value;

            if (eventCategoryName.trim() != "" && eventCategoryColor != "") {
                this.createEventFormSubmit = true;
                this.disableFormControls();
                const body = {
                    lang: this.language,
                    categoryName: eventCategoryName,
                    colorCode: eventCategoryColor,
                    academicId: this.eventCategoryService.academicId
                };
                const response: any = await firstValueFrom(
                    this.eventCategoryService.createEventCategory(body));
                this.createEventFormSubmit = false;
                this.enableFormControls();
                if (response?.status) {
                    if (response?.body?.message) {
                        customToastr(
                            response?.body?.message,
                            "success"
                        );
                    }
                    else {
                        const createEventCategorySuccessMessageTranslation = await firstValueFrom(this.translate.get('createEventCategorySuccessMessage')); 
                        customToastr(
                            createEventCategorySuccessMessageTranslation,
                            "success"
                        );
                    }

                    this.closePopup();
                    this.getEventCategory();
                }
                else {
                    const CategoryFailedToCreateErrorTextTranslation = await firstValueFrom(this.translate.get('CategoryFailedToCreateErrorText')); 
                    customToastr(CategoryFailedToCreateErrorTextTranslation, "error");
                }
            }
        }
        catch (error) {
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const CategoryFailedToCreateErrorTextTranslation = await firstValueFrom(this.translate.get('CategoryFailedToCreateErrorText')); 
                customToastr(CategoryFailedToCreateErrorTextTranslation, "error");
            }
            this.createEventFormSubmit = false;
            this.enableFormControls();
        }
    }

    // Function for get the event category color list
    async getEventCategoryColorList() {
        try {
            const response: any = await firstValueFrom(
                this.eventCategoryService.getColorListAPI()
            );
            if (response?.status) {
                this.colorList = response?.body?.colorOptions;
            }
        }
        catch (error) {
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const somethingWentWrongTranslation = await firstValueFrom(this.translate.get('somethingWentWrong')); 
                customToastr(somethingWentWrongTranslation, "error");
            }
        }
    }

    // Function for delete event category 
    async deleteEventCategory() {
        try {
            const body = {
                lang: this.language,
                eventCategoryId: this.categoryId,
                isDeleteConfirmed: null,
                academicId: this.eventCategoryService.academicId
            };
            this.createEventFormSubmit = true;
            const response: any = await firstValueFrom(this.eventCategoryService.deleteEventCategory(body));
            this.createEventFormSubmit = false;
            if (response?.status) {
                const confirmationPopup = response?.body?.isConfirmationRequired;
                this.conflictCategoryData = response?.body?.isEventScheduledWithCategoryResponse;
                if (confirmationPopup) {
                    this.confirmDeletePopup = true;
                }
                else {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, "success");
                    }
                    else {
                        const deletEventCategorySuccessfullTranslation = await firstValueFrom(this.translate.get('deletEventCategorySuccessfull')); 
                        customToastr(deletEventCategorySuccessfullTranslation, "success");
                    }
                    this.closePopup();
                    this.getEventCategory();
                }
            }
        }
        catch (error) {
            this.createEventFormSubmit = false;
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const deleteEventCategoryErrorTranslation = await firstValueFrom(this.translate.get('deleteEventCategoryError')); 
                customToastr(deleteEventCategoryErrorTranslation, "error");
            }
        }
    }

    // Function for confirmation of the delete event category when any event is scheduled
    async confirmDeleteEventCategory() {
        try {
            const body = {
                lang: this.language,
                eventCategoryId: this.categoryId,
                isDeleteConfirmed: "yes",
                academicId: this.eventCategoryService.academicId,
            };
            this.confirmDeletePopupForm = true;
            const response: any = await firstValueFrom(this.eventCategoryService.deleteEventCategory(body));
            this.confirmDeletePopupForm = false;
            if (response?.status) {
                this.getEventCategory();
                if (response?.body?.message) {
                    customToastr(response?.body?.message, "success");
                }
                else {
                    const deletEventCategorySuccessfullTranslation = await firstValueFrom(this.translate.get('deletEventCategorySuccessfull')); 
                    customToastr(deletEventCategorySuccessfullTranslation, "success");
                }
            }
            else {
                const deleteEventCategoryErrorTranslation = await firstValueFrom(this.translate.get('deleteEventCategoryError')); 
                customToastr(deleteEventCategoryErrorTranslation, "error");
            }
            this.closePopup();
        }
        catch (error) {
            this.confirmDeletePopupForm = false;
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const deleteEventCategoryErrorTranslation = await firstValueFrom(this.translate.get('deleteEventCategoryError')); 
                customToastr(deleteEventCategoryErrorTranslation, "error");
            }
        }
    }
}
